import React, { useContext, useEffect, useRef, useState } from 'react';
import { Price } from 'src/components/Helpers/Price';
import { tap, withLatestFrom } from 'rxjs';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { useBreakpointMatch } from 'src/hooks/useBreakpoint';
import classnames from 'classnames';

import { Button } from '../Button/Button';
import { MobileCreditBalance } from '../Customer/MobileCreditBalance';

import classes from './ProductActions.scss';
import { ProductDetailContext } from './ProductDetailContext';

type Props = {
    isPending: boolean;
    disabled: boolean;
    qtySelected: React.ReactNode;
    itemPrice: number | null;
    totalPrice: number;
    vat: boolean;
    canCustomise: boolean;
};

export const ProductActions: React.FC<Props> = (props) => {
    const { qtySelected, isPending, totalPrice } = props;
    const { isMob, isDesk } = useBreakpointMatch();
    const actionsRef = useRef<HTMLDivElement>(null);
    const [isFooterVisible, setIsFooterVisible] = useState(false);

    useEffect(() => {
        if (!isMob || typeof window === 'undefined' || typeof document === 'undefined') {
            setIsFooterVisible(false);
            return undefined;
        }

        const browserWindow = window;
        const footer = document.querySelector('footer');
        if (!footer) return undefined;

        const updateFooterVisibility = () => {
            const footerRect = footer.getBoundingClientRect();
            setIsFooterVisible(footerRect.top < browserWindow.innerHeight);
        };

        if (browserWindow.IntersectionObserver) {
            const observer = new browserWindow.IntersectionObserver(
                ([entry]) => setIsFooterVisible(entry.isIntersecting),
                { rootMargin: '0px 0px 24px 0px' },
            );

            observer.observe(footer);
            return () => observer.disconnect();
        }

        updateFooterVisibility();
        browserWindow.addEventListener('scroll', updateFooterVisibility, { passive: true });
        browserWindow.addEventListener('resize', updateFooterVisibility);

        return () => {
            browserWindow.removeEventListener('scroll', updateFooterVisibility);
            browserWindow.removeEventListener('resize', updateFooterVisibility);
        };
    }, [isMob]);

    useEffect(() => {
        if (!isMob || typeof window === 'undefined') return undefined;

        const browserWindow = window;
        const visualViewport = browserWindow.visualViewport;
        if (!visualViewport) return undefined;

        let animationFrame: number | null = null;
        let timeouts: number[] = [];

        const updateFixedPosition = () => {
            animationFrame = null;

            if (!actionsRef.current) return;

            const viewportBottom = visualViewport.offsetTop + visualViewport.height;
            const top = Math.max(0, Math.round(viewportBottom - actionsRef.current.offsetHeight));

            actionsRef.current.style.setProperty('--product-actions-fixed-top', `${top}px`);
            actionsRef.current.style.setProperty('--product-actions-fixed-bottom', 'auto');
        };

        const scheduleFixedPositionUpdate = () => {
            if (animationFrame !== null) browserWindow.cancelAnimationFrame(animationFrame);
            animationFrame = browserWindow.requestAnimationFrame(updateFixedPosition);
        };

        const handleViewportChange = () => {
            timeouts.forEach((timeout) => browserWindow.clearTimeout(timeout));
            timeouts = [0, 50, 150, 300].map((delay) => browserWindow.setTimeout(scheduleFixedPositionUpdate, delay));
        };

        handleViewportChange();
        visualViewport.addEventListener('resize', handleViewportChange);
        visualViewport.addEventListener('scroll', handleViewportChange);
        browserWindow.addEventListener('orientationchange', handleViewportChange);

        return () => {
            if (animationFrame !== null) browserWindow.cancelAnimationFrame(animationFrame);
            timeouts.forEach((timeout) => browserWindow.clearTimeout(timeout));
            visualViewport.removeEventListener('resize', handleViewportChange);
            visualViewport.removeEventListener('scroll', handleViewportChange);
            browserWindow.removeEventListener('orientationchange', handleViewportChange);
        };
    }, [isMob]);

    return (
        <div
            ref={actionsRef}
            className={classnames(classes.productActions, 'product-actions-fixed', {
                [classes.productActionsHidden]: isMob && isFooterVisible,
            })}
        >
            {isMob && (
                <MobileCreditBalance
                    showBottomDivider
                    contentCentered
                    noPadding
                    className={classes.paddedCreditBalance}
                />
            )}
            <div className={classes.productActionsContent}>
                <div className={classnames(classes.productActionsAdd, classes.productActionsAddNew)}>
                    {isMob && (
                        <div className={classes.productActionsTotal}>
                            <Price price={totalPrice} priceIncl={totalPrice} />
                        </div>
                    )}
                    <Button type="submit" className={classes.productActionsSubmit} disabled={props.disabled}>
                        {isPending && 'Please wait..'}
                        {!isPending && (
                            <>
                                {isDesk && (
                                    <>
                                        Add&nbsp;<strong>{qtySelected}</strong>&nbsp;to basket{' '}
                                    </>
                                )}
                                {isMob && 'Add to basket'}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const ProductActionsSummary = (props) => {
    const { totalPrice, isPending, disabled } = props;
    return (
        <div className={classes.productActionsNew}>
            <div className={classes.productActionsTotalNew}>
                <h2 className={classes.bundleSummaryTitle}>Total</h2>
                <Price price={totalPrice} priceIncl={totalPrice} />
            </div>

            <div className={classes.productActionsAdd}>
                <Button type="submit" className={classes.productActionsSubmit} disabled={disabled}>
                    {isPending && 'Please wait..'}
                    {!isPending && 'Add to basket'}
                </Button>
            </div>
        </div>
    );
};

type ConnectedProps = {
    isPending: boolean;
    canCustomise: boolean;
};
export const ProductActionsConnected: React.FC<ConnectedProps> = React.memo((props) => {
    const { isPending } = props;
    const { qty$, itemPrice$ } = useContext(ProductDetailContext);
    const { vat } = useVatCausesRender();

    const [{ qtySelected, itemPrice, totalPrice }, setPrices] = useState({
        qtySelected: qty$.value,
        itemPrice: itemPrice$.value,
        totalPrice: qty$.value * itemPrice$.value,
    });

    useEffect(() => {
        const price = itemPrice$.pipe(
            withLatestFrom(qty$),
            tap(([itemPrice, qty]) => {
                setPrices({
                    qtySelected: qty,
                    itemPrice,
                    totalPrice: qty * itemPrice,
                });
            }),
        );
        const sub = price.subscribe();
        return () => sub.unsubscribe();
    }, [itemPrice$, qty$]);

    return (
        <ProductActions
            qtySelected={`${qtySelected} Items`}
            itemPrice={itemPrice}
            totalPrice={totalPrice}
            isPending={isPending}
            vat={vat}
            disabled={isPending || qtySelected === 0}
            canCustomise={props.canCustomise}
        />
    );
});
